import {inject, Injectable} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {Params} from '@angular/router';
import {Observable} from 'rxjs';
import {ApiService} from '@aviary/services/api.service';
import {DateOnlyIsoString, ListData} from '@aviary/store/common/common.types';
import {buildQueryParamsFrom} from '@aviary/store/common/list-data.helpers';
import {CatalogRecordIdsPayload, PatronHoldCreatePayload, PauseAllHoldsPayload, PauseAllHoldsResponse, PauseHoldPayload} from '@store/holds/holds.api-types';
import {Hold, ON_HOLD_HOLD_STATUSES, ReadyForPickupHolds} from '@store/holds/holds.types';
import {
  contextWithErrorHandlerForCancelHolds,
  contextWithErrorHandlerForLoadHoldsForCatalogRecords,
  contextWithErrorHandlerForPauseAllHolds,
  contextWithErrorHandlerForPauseHold,
  contextWithErrorHandlerForPlaceHold,
  contextWithErrorHandlerForResumeHolds,
  contextWithErrorHandlerForUpdateHoldPickupBranch
} from '@store/holds/holds.handlers';

@Injectable({providedIn: 'root'})
export class HoldsService {

  private readonly apiService: ApiService = inject(ApiService);

  public loadReadyForPickupHolds(): Observable<ReadyForPickupHolds> {
    const endpoint: string = 'patrons/v5/holds/ready-for-pickup';

    return this.apiService.getSingleObject(endpoint);
  }

  public loadOnHoldHolds(queryParams: Params): Observable<ListData<Hold>> {
    const endpoint: string = 'patrons/v5/holds';

    const params: HttpParams = buildQueryParamsFrom({
      ...queryParams,
      statuses: ON_HOLD_HOLD_STATUSES
    });

    return this.apiService.getListData(endpoint, {params});
  }

  public loadHoldsForCatalogRecords(catalogRecordIds: number[]): Observable<Hold[]> {
    const endpoint: string = 'patrons/v2/holds/outstanding/by-catalog-records';
    const body: CatalogRecordIdsPayload = {catalogRecordIds};

    return this.apiService.postAndReturnObjects(endpoint, body, {context: contextWithErrorHandlerForLoadHoldsForCatalogRecords()});
  }

  public placeHold(catalogRecordId: number, pickupBranchId: number): Observable<Hold> {
    const endpoint: string = 'patrons/v5/holds';
    const body: PatronHoldCreatePayload = {catalogRecordId, pickupBranchId};

    return this.apiService.postAndReturnSingleObject(endpoint, body, {context: contextWithErrorHandlerForPlaceHold()});
  }

  public updateHoldPickupBranch(holdId: number, pickupBranchId: number): Observable<void> {
    const endpoint: string = `patrons/v5/holds/${holdId}/pickup-branch`;
    const params: HttpParams = buildQueryParamsFrom({pickupBranchId});

    return this.apiService.patch(endpoint, {}, {params, context: contextWithErrorHandlerForUpdateHoldPickupBranch()});
  }

  public pauseHold(holdIds: number[], notWantedBefore: DateOnlyIsoString): Observable<void> {
    const endpoint: string = 'patrons/v2/holds/pause';
    const body: PauseHoldPayload = {holdIds, notWantedBefore};

    return this.apiService.patch(endpoint, body, {context: contextWithErrorHandlerForPauseHold()});
  }

  public resumeHolds(holdIdList: number[]): Observable<void> {
    const endpoint: string = 'patrons/v2/holds/resume';
    return this.apiService.patch(endpoint, holdIdList, {context: contextWithErrorHandlerForResumeHolds()});
  }

  public cancelHolds(itemHoldIds: number[]): Observable<void> {
    const endpoint: string = 'patrons/v5/holds';
    const params: HttpParams = buildQueryParamsFrom({itemHoldIds});
    return this.apiService.patch(endpoint, {}, {params, context: contextWithErrorHandlerForCancelHolds()});
  }

  public pauseAllHolds(pauseUntil: DateOnlyIsoString): Observable<PauseAllHoldsResponse> {
    const endpoint: string = 'patrons/v2/holds/pause-all';
    const body: PauseAllHoldsPayload = {pauseUntil};
    return this.apiService.patchAndReturnSingleObject(endpoint, body, {context: contextWithErrorHandlerForPauseAllHolds()});
  }
}
